import { useState } from "react";

import { DashboardScheduleWeekTaskCategoryHeader } from "./DashboardScheduleWeekTaskCategoryChip";
import { DashboardTaskAddButton } from "./DashboardTaskAddButton";
import { DashboardTaskRow } from "./DashboardTaskRow";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

import {
  dashboardScheduleTaskCategoryLabels,
  dashboardScheduleWeek,
} from "@/lib/dashboardScheduleWeek";
import { cn } from "@/lib/utils";
import { DashboardScheduleTaskCategory, DashboardTask } from "@/types";

// Per-section empty copy — a little encouragement when nothing's missing a start.
const emptyLabel = (category: DashboardScheduleTaskCategory): string => {
  if (category === DashboardScheduleTaskCategory.GENERAL)
    return "No general tasks";
  const base = `No ${dashboardScheduleTaskCategoryLabels[category].toLowerCase()}`;
  return category === DashboardScheduleTaskCategory.MISSING_START
    ? `${base} :)`
    : base;
};

export const DashboardTaskCategorySection = ({
  category,
  dayTasks,
  day,
  variant = "compact",
  showOwnerAvatar = false,
  onAddTask,
}: {
  category: DashboardScheduleTaskCategory;
  dayTasks: DashboardTask[];
  day: Date;
  variant?: "compact" | "comfortable";
  showOwnerAvatar?: boolean;
  // Provided only for the General section — enables the inline quick-add.
  onAddTask?: (title: string) => void;
}) => {
  /* React Hooks */
  const [isAdding, setIsAdding] = useState(false);

  /* Helpers */
  const isComfortable = variant === "comfortable";
  const categoryTasks = dayTasks.filter(
    (task) => dashboardScheduleWeek.listTaskCategory(task.type) === category,
  );

  /* Handlers */
  const handleStartAdd = () => {
    setIsAdding(true);
  };

  // Persists the typed title (if any) via the parent's create handler.
  const handleSave = (rawTitle: string) => {
    const trimmed = rawTitle.trim();
    if (trimmed && onAddTask) onAddTask(trimmed);
  };

  // Enter saves and keeps the field open for rapid entry; Escape/blur closes it.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave(e.currentTarget.value);
    } else if (e.key === "Escape") {
      setIsAdding(false);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    handleSave(e.currentTarget.value);
    setIsAdding(false);
  };

  /* Render */
  // Mirrors a real task row (checkbox + text position) so nothing shifts when
  // the typed task saves and the row swaps in.
  const renderAddInput = () => {
    if (!isAdding) return null;
    return (
      <Row
        className={cn(
          "items-center",
          isComfortable ? "gap-2.5 px-2 py-2" : "gap-1.5 px-1.5 py-0.5",
        )}
      >
        <Checkbox
          checked={false}
          className={cn(
            "shrink-0 rounded-full",
            isComfortable ? "h-4 w-4" : "h-3.5 w-3.5 [&_svg]:h-3 [&_svg]:w-3",
          )}
        />
        <Input
          // Uncontrolled + keyed by row count: typed text stays put until the
          // optimistic row renders — that same render remounts a fresh empty input.
          key={categoryTasks.length}
          autoFocus
          // Bare typing line — no box, just text where the task title sits.
          className={cn(
            // Override the Input's md:text-sm too, or compact reverts to 14px on desktop.
            "min-w-0 flex-1 border-none bg-transparent p-0 text-slate-700 shadow-none placeholder:text-slate-400 focus-visible:ring-0 focus-visible:ring-offset-0",
            isComfortable
              ? "h-6 text-sm md:text-sm"
              : "h-5 text-[11px] md:text-[11px]",
          )}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="Add a task…"
        />
      </Row>
    );
  };

  const renderTasks = () => {
    if (categoryTasks.length === 0 && !isAdding) {
      return (
        <Text
          className={cn(
            "italic text-slate-400",
            isComfortable ? "px-4 py-2 text-sm" : "text-[11px]",
          )}
        >
          {emptyLabel(category)}
        </Text>
      );
    }
    return (
      <Column className={isComfortable ? "px-4 py-1" : "gap-0.5"}>
        {categoryTasks.map((task) => (
          <DashboardTaskRow
            key={task.id}
            day={day}
            showOwnerAvatar={showOwnerAvatar}
            task={task}
            variant={variant}
          />
        ))}
        {renderAddInput()}
      </Column>
    );
  };

  const render = () => {
    if (isComfortable) {
      return (
        <Column>
          <DashboardScheduleWeekTaskCategoryHeader
            category={category}
            onAddClick={handleStartAdd}
            showAddButton={onAddTask != null}
          />
          {renderTasks()}
        </Column>
      );
    }
    return (
      <Column className="gap-1.5">
        <Row className="items-center justify-between">
          <Text className="shrink-0 text-[11px] font-semibold text-slate-500">
            {dashboardScheduleTaskCategoryLabels[category]}
          </Text>
          {onAddTask != null ? (
            <DashboardTaskAddButton onClick={handleStartAdd} />
          ) : null}
        </Row>
        {renderTasks()}
      </Column>
    );
  };

  return render();
};
